/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import {
  Grid,
  Select,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControl,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import {
  filtersGridStyles,
  viewTypographyStyles,
  selectBaseStyles,
  datePickerStyles,
} from "../styles";
import { ResetButton } from "../../../components/reset_button";
import { UpdateButton } from "../../../components/update_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import ClearIcon from '@mui/icons-material/Clear';
import { StyledMenuItem } from "../../../components/styled_menu_item";
import dayjs from "dayjs";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";
import { mergeUniqueByKey } from "../../../utils/dropdownInfiniteQuery";
import snakeCase from "../../../utils/snakeCase";

// Each rendered filter chip needs its own test id so its close button is locatable.
const getFilterChipTestId = (option) =>
  option?.testId || `${snakeCase(option?.key || '').replace(/_/g, '-')}-filter-chip`

const PerqOptions = [ 
  {
    label: 'All',
    value: false,
  },
  {
    label: 'OwnersTable',
    value: true,
  }
]

const FilterSection = ({
  filters,
  handleFilterChange,
  moreOptionsConfig,
  selectOptions,
  handleMultiSelectChange,
  employeeSearch,
  setEmployeeSearch,
  employees,
  employeeFetching,
  employeesFetchingNextPage,
  hasNextEmployeesPage,
  employeeMenuRef,
  employeeLoaderRef,
  restaurants,
  restaurantFetching,
  restaurantsFetchingNextPage,
  hasNextRestaurantsPage,
  restaurantMenuRef,
  restaurantLoaderRef,
  allRestaurants,
  restaurantGroups,
  restaurantGroupFetching,
  restaurantGroupsFetchingNextPage,
  hasNextRestaurantGroupsPage,
  restaurantGroupMenuRef,
  restaurantGroupLoaderRef,
  allRestaurantGroups,
  showRestaurantSubGroupFilter = false,
  restaurantSubGroups,
  restaurantSubGroupFetching,
  hasNextRestaurantSubGroupsPage,
  restaurantSubGroupMenuRef,
  restaurantSubGroupLoaderRef,
  allRestaurantSubGroups,
  setRestaurantSubGroupSearch,
  handleRestaurantSubGroupChange,
  handleRestaurantSubGroupClear,
  renderFormattedValue,
  setFilters,
  setFiltersChanged,
  fetchingDataLoader,
  fetchData,
  resetFilters,
  filtersChanged,
  setQueryFilter,
  setUpdateData,
  restaurantSearch,
  setRestaurantSearch,
  restaurantGroupSearch,
  setRestaurantGroupSearch,
  handleOwnersTableDiscount,
  handleRestaurantClear,
  handleRestaurantGroupClear,
  handleEmployeeChange,
  employeeSelectValue,
  employeeDisplayValue,
  setOrderBy,
  setOrder,
  setPage,
}) => {
  const getDiscountLabel = (data) => {
    if(data) {
      return 'OwnersTable'
    }
    else {
      return 'All'
    }
  }

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = allRestaurantGroups?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value  : ''
  }

  const selectedRestaurantSubGroup = allRestaurantSubGroups?.find(
    (sub) => `${sub?.value}` === `${filters?.restaurantSubGroupId}`
  )

  // Keep the selected sub group in the list so the Select always has a matching
  // option, even when a search excludes it.
  const displayedRestaurantSubGroups = selectedRestaurantSubGroup
    ? mergeUniqueByKey(restaurantSubGroups || [], [selectedRestaurantSubGroup], 'value')
    : (restaurantSubGroups || [])

  const getRestaurantSubGroupName = (id) => {
    const restaurantSubGroup = allRestaurantSubGroups?.find((sub) => `${sub?.value}` === `${id}`)
    return restaurantSubGroup?.label || ''
  }

  const getRestaurantName = (id) => {
    const restaurant = allRestaurants?.find((res) => res.id === id)
    return restaurant?.name || ''
  }

  const hasSingleAvailableEmployee = !employeeSearch && employees?.length === 1 && !hasNextEmployeesPage
  const resolvedEmployeeValue = hasSingleAvailableEmployee
    ? employees?.[0]?.value
    : (employeeSelectValue ?? filters?.employees)
  const resolvedEmployeeDisplayValue = hasSingleAvailableEmployee
    ? (employees?.[0]?.label || 'All Employees')
    : (employeeDisplayValue ?? filters?.employees)
  const onEmployeeChange = handleEmployeeChange || ((event) => handleFilterChange("employees", event.target.value))
  const isCustomDateRangeValid = 
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled = 
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleDateFilterChange = (value) => {
    setFilters?.((prev) => ({
      ...prev,
      dateFilter: value,
      customDateFrom: null,
      customDateTo: null,
    }));
    setFiltersChanged?.(true);
    setUpdateData?.(false);
  };

  const handleCustomDateFromChange = (date) => {
    setFilters?.((prev) => {
      if (!date) {
        return {
          ...prev,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      return {
        ...prev,
        customDateFrom: date,
        customDateTo:
          isValidDayjsValue(date) && !isValidDayjsValue(prev.customDateTo)
            ? dayjs().startOf("day")
            : prev.customDateTo,
      };
    });
    setFiltersChanged?.(true);
    setUpdateData?.(false);
  };

  const handleCustomDateToChange = (date) => {
    setFilters?.((prev) => ({
      ...prev,
      customDateTo: date,
    }));
    setFiltersChanged?.(true);
    setUpdateData?.(false);
  };

  return (
    <Grid
      container
      spacing={1}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      sx={filtersGridStyles}
      data-testid="orders-modifiers-filter-section"
    >
      { /* Filter Section */ }
      <Grid 
        item 
        xs={12} 
        md={9} 
        lg={10}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 1,
            flexWrap: "wrap",
            "& > *": {
              flexShrink: 0
            }
          }}
        >
          <Typography variant="body2" sx={viewTypographyStyles}>
            View
          </Typography>
          
          {/* Date Filter */}
          <Select
            value={filters?.dateFilter}
            renderValue={() => filters?.dateFilter}
            size="small"
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: { sx: { width: "230px" } },
            }}
            sx={{ 
              ...selectBaseStyles, 
              minWidth: "120px",
              width: { xs: "100%", sm: "auto" }
            }}
            onChange={(e) => handleDateFilterChange(e.target.value)}
          >
            {selectOptions["Days"].map((value) => (
              <StyledMenuItem
                value={value}
                key={value}
              >
                {filters?.dateFilter === value ? (
                  <SelectedMenuItem data={value} />
                ) : (
                  <>{value}</>
                )}
              </StyledMenuItem>
            ))}
          </Select>

          {/* Custom Date Range */}
          {filters?.dateFilter === "Custom Date" && (
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: { xs: "100%", sm: "auto" }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  Days
                </Typography>
                <DatePicker
                  format="MM-DD-YYYY"
                  sx={{
                    ...datePickerStyles,
                    width: { xs: "100%", sm: "140px", lg: '160px' }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      "data-testid": "custom-date-from-field",
                      inputProps: { "data-testid": "custom-date-from-input" },
                    },
                    openPickerButton: { "data-testid": "custom-date-from-picker-button" },
                    openPickerIcon: { "data-testid": "custom-date-from-calendar-icon" },
                  }}
                  value={filters?.customDateFrom || null}
                  onChange={handleCustomDateFromChange}
                />
                <Typography variant="body2" sx={viewTypographyStyles}>
                  through
                </Typography>
                <DatePicker
                  sx={{
                    ...datePickerStyles,
                    width: { xs: "100%", sm: "140px", lg: '160px' }
                  }}
                  format="MM-DD-YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      "data-testid": "custom-date-to-field",
                      inputProps: { "data-testid": "custom-date-to-input" },
                    },
                    openPickerButton: { "data-testid": "custom-date-to-picker-button" },
                    openPickerIcon: { "data-testid": "custom-date-to-calendar-icon" },
                  }}
                  value={filters?.customDateTo || null}
                  onChange={handleCustomDateToChange}
                />
              </LocalizationProvider>
            </Box>
          )}

          {/* Hours Filter */}
          <Select
            value={filters?.hours}
            renderValue={() => filters?.hours}
            size="small"
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: { sx: { width: "230px" } },
            }}
            sx={{ 
              ...selectBaseStyles, 
              minWidth: { xs: "120px", sm: "140px" },
              width: { xs: "100%", sm: "auto" }
            }}
            onChange={(e) => handleFilterChange("hours", e.target.value)}
          >
            {selectOptions["Hours"].map((value) => (
              <StyledMenuItem
                value={value}
                key={value}
              >
                {filters?.hours === value ? (
                  <SelectedMenuItem data={value} />
                ) : (
                  <>{value}</>
                )}
              </StyledMenuItem>
            ))}
          </Select>

          {/* Custom Hours Range */}
          {filters?.hours === "Custom Hours" && (
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: { xs: "100%", sm: "auto" }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  Hours
                </Typography>
                <TimePicker
                  ampm={true}
                  format="hh:mm a"
                  value={filters?.customHoursFrom}
                  onChange={(time) =>
                    handleFilterChange("customHoursFrom", time)
                  }
                  slots={{ openPickerButton: null }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "hh:mm am",
                      "data-testid": "custom-hours-from-field",
                      inputProps: {
                        placeholder: "hh:mm am",
                        "data-testid": "custom-hours-from-input",
                      },
                      sx: {
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px" },
                      },
                    },
                    openPickerButton: { "data-testid": "custom-hours-from-picker-button" },
                    openPickerIcon: { "data-testid": "custom-hours-from-clock-icon" },
                  }}
                />
                <Typography variant="body2" sx={viewTypographyStyles}>
                  through
                </Typography>
                <TimePicker
                  ampm={true}
                  format="hh:mm a"
                  value={filters?.customHoursTo}
                  onChange={(time) => handleFilterChange("customHoursTo", time)}
                  slots={{ openPickerButton: null }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "hh:mm am",
                      "data-testid": "custom-hours-to-field",
                      inputProps: {
                        placeholder: "hh:mm am",
                        "data-testid": "custom-hours-to-input",
                      },
                      sx: {
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px" },
                      },
                    },
                    openPickerButton: { "data-testid": "custom-hours-to-picker-button" },
                    openPickerIcon: { "data-testid": "custom-hours-to-clock-icon" },
                  }}
                />
              </LocalizationProvider>
            </Box>
          )}

          <Typography variant="body2" sx={viewTypographyStyles}>
            at
          </Typography>

          {/* Restaurant Groups */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Select
              size="small"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: {
                  ref: restaurantGroupMenuRef,
                  sx: { maxHeight: "40vh" },
                },
                autoFocus: false,
                MenuListProps: { sx: { pt: 0 } },
              }}
              sx={{
                ...selectBaseStyles,
                minWidth: { xs: "100%", sm: "180px" },
                borderColor: "#2b4fb9",
              }}
              onChange={(e) => handleFilterChange("restaurantGroupId", e.target.value)}
              onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
              value={filters?.restaurantGroupId || ""}
              renderValue={() =>
                filters?.restaurantGroupId === '' ? 'All Restaurant Groups' : getRestaurantGroupName(filters?.restaurantGroupId)
              }
              displayEmpty
              endAdornment={
                filters?.restaurantGroupId && restaurantGroups?.length !== 1 ? (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                    <IconButton 
                      edge='end' 
                      size="small" 
                      disableRipple
                      onClick={handleRestaurantGroupClear}
                    >
                      <ClearIcon sx={{fontSize: '20px'}}/>
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
              {restaurantGroups
                ?.map((res) => (
                  <StyledMenuItem
                    key={res?.label}
                    value={res?.label}
                  >
                    {filters?.restaurantGroupId === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value}
                  </StyledMenuItem>
                ))}
              <DropdownInfiniteLoader
                hasNextPage={hasNextRestaurantGroupsPage}
                targetRef={restaurantGroupLoaderRef}
              />
            </Select>
          </FormControl>

          {/* Restaurant Sub Groups */}
          {showRestaurantSubGroupFilter && (
            <FormControl
              size="small"
              sx={{ width: { xs: "100%", sm: "auto" } }}
              disabled={!filters?.restaurantGroupId}
            >
              <Select
                data-testid="orders-restaurant-sub-group-filter"
                size="small"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    ref: restaurantSubGroupMenuRef,
                    sx: { maxHeight: "40vh" },
                  },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "180px" },
                }}
                onChange={handleRestaurantSubGroupChange}
                onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                value={filters?.restaurantSubGroupId || ""}
                renderValue={() =>
                  filters?.restaurantSubGroupId
                    ? getRestaurantSubGroupName(filters?.restaurantSubGroupId)
                    : 'All Sub Groups'
                }
                displayEmpty
                endAdornment={
                  filters?.restaurantSubGroupId ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantSubGroupClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search sub group'} isFetching={restaurantSubGroupFetching} />
                {displayedRestaurantSubGroups
                  ?.map((subGroup) => (
                    <StyledMenuItem
                      key={subGroup?.value}
                      value={subGroup?.value}
                    >
                      {`${filters?.restaurantSubGroupId}` === `${subGroup?.value}`
                        ? <SelectedMenuItem data={subGroup?.label} />
                        : subGroup?.label}
                    </StyledMenuItem>
                  ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantSubGroupsPage}
                  targetRef={restaurantSubGroupLoaderRef}
                />
              </Select>
            </FormControl>
          )}

          {/* Restaurants */}
          <Select
            data-testid="report-restaurants-filter"
            multiple
            value={filters?.restaurantIds}
            size="small"
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                ref: restaurantMenuRef,
                sx: { maxHeight: "40vh" },
              },
              autoFocus: false,
              MenuListProps: { sx: { pt: 0 } },
            }}
            displayEmpty
            renderValue={(selected) =>
              selected.length < 1
                ? "All Restaurants"
                : selected.length === 1
                ? getRestaurantName(selected[0])
                : `${selected.length} Selected`
            }
            sx={{ 
              ...selectBaseStyles, 
              minWidth: { xs: "100%", sm: "210px" },
              width: { xs: "100%", sm: "auto" }
            }}
            onChange={handleMultiSelectChange("restaurantIds")}
            onClose={() => setTimeout(() => setRestaurantSearch(""), 1000)}
            endAdornment={
              filters?.restaurantIds?.length > 0 && restaurants?.length !== 1 ? (
                <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                  <IconButton 
                    edge='end' 
                    size="small" 
                    disableRipple
                    onClick={handleRestaurantClear}
                  >
                    <ClearIcon sx={{fontSize: '20px'}}/>
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          >
            <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
            {restaurants
              .map((res) => (
                <StyledMenuItem
                  key={res?.id}
                  value={res?.id}
                >
                  {filters?.restaurantIds?.includes(res?.id) ? (
                    <SelectedMenuItem data={res?.name} />
                  ) : (
                    <>{`${res?.name}`}</>
                  )}
                </StyledMenuItem>
              ))}
            <DropdownInfiniteLoader
              hasNextPage={hasNextRestaurantsPage}
              targetRef={restaurantLoaderRef}
            />
          </Select>

          <Typography variant="body2" sx={viewTypographyStyles}>
            for
          </Typography>
          
          {/* Employees */}
          <Select
            data-testid="report-employees-filter"
            value={resolvedEmployeeValue}
            renderValue={() => resolvedEmployeeDisplayValue}
            size="small"
            sx={{ 
              ...selectBaseStyles, 
              minWidth: { xs: "100%", sm: "140px" },
              width: { xs: "100%", sm: "auto" }
            }}
            onChange={onEmployeeChange}
            onClose={() => setTimeout(() => setEmployeeSearch(""), 1000)}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                ref: employeeMenuRef,
                sx: { maxHeight: "40vh" },
              },
              autoFocus: false,
              MenuListProps: { sx: { pt: 0 } },
            }}
          >
            <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
            {!hasSingleAvailableEmployee && (
              <StyledMenuItem
                value="All Employees"
              >
                {resolvedEmployeeValue === 'All Employees' ? (
                  <SelectedMenuItem data={'All Employees'} />
                ) : (
                  'All Employees'
                )}
              </StyledMenuItem>
            )}
            {employees
              .map((employee) => (
                <StyledMenuItem
                  key={employee?.value}
                  value={employee?.value}
                >
                  {resolvedEmployeeValue === employee?.value ? (
                    <SelectedMenuItem data={employee?.label} />
                  ) : (
                    <>{employee?.label}</>
                  )}
                </StyledMenuItem>
              ))}
            <DropdownInfiniteLoader
              hasNextPage={hasNextEmployeesPage}
              targetRef={employeeLoaderRef}
            />
          </Select>

          {/* Discounts */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Select
              value={filters?.has_ot_discount ?? ''}
              renderValue={() => filters?.has_ot_discount === null ? 'Perqs' : getDiscountLabel(filters?.has_ot_discount)}
              size="small"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: { sx: { maxHeight: "40vh", width: '230px' } },
                autoFocus: false,
              }}
              displayEmpty
              sx={{ 
                ...selectBaseStyles, 
                minWidth: { xs: "100%", sm: "140px" }
              }}
              onChange={handleOwnersTableDiscount}
            >
              <StyledMenuItem value={false}>
                {filters?.has_ot_discount === false ? <SelectedMenuItem data={'All'} /> : 'All'}
              </StyledMenuItem>
              <StyledMenuItem value={true}>
                {filters?.has_ot_discount ? <SelectedMenuItem data={'OwnersTable'} /> : 'OwnersTable'}
              </StyledMenuItem>
            </Select>
          </FormControl>

          {/* More Options */}
          <Select
            multiple
            value={filters["moreOptions"]}
            displayEmpty
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
            onChange={handleMultiSelectChange("moreOptions")}
            renderValue={(selected) => "More"}
            sx={{ 
              ...selectBaseStyles, 
              minWidth: { xs: "100%", sm: "80px" },
              width: { xs: "100%", sm: "auto" }
            }}
          >
            {moreOptionsConfig.map((option) => (
              <StyledMenuItem
                key={option.key}
                value={option.key}
              >
                <Checkbox
                  checked={filters["moreOptions"].includes(option.key)}
                />
                {option.label}
              </StyledMenuItem>
            ))}
          </Select>
        </Box>

        {/* Dynamic select fields */}
        <Box 
          sx={{ 
            display: "flex", 
            gap: 2, 
            flexWrap: "wrap",
            mt: 1
          }}
        >
          {filters.moreOptions.map((optionKey) => {
            const option = moreOptionsConfig.find(
              (opt) => opt.key === optionKey
            );
            const chipTestId = getFilterChipTestId(option);
            return (
              <Box
                key={optionKey}
                data-testid={chipTestId}
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <Select
                  multiple
                  value={filters[optionKey]}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                  displayEmpty
                  onChange={handleMultiSelectChange(optionKey)}
                  renderValue={(selected) =>
                    renderFormattedValue(selected, option)
                  }
                  sx={{ 
                    ...selectBaseStyles, 
                    minWidth: { xs: "calc(100% - 48px)", sm: "120px" }
                  }}
                >
                  {selectOptions[option.label].map((value) => (
                    <StyledMenuItem
                      key={value}
                      value={value}
                    >
                      <Checkbox
                        checked={filters[optionKey].includes(value)}
                      />
                      {value}
                    </StyledMenuItem>
                  ))}
                </Select>
                <IconButton
                  size="small"
                  data-testid={`${chipTestId}-close-button`}
                  sx={{
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    borderRadius: 1,
                    height: "100%",
                    width: '40px'
                  }}
                  onClick={() => {
                    setFilters((prev) => ({
                      ...prev,
                      importedOrderIds: [],
                      [optionKey]: [],
                      moreOptions: prev.moreOptions.filter(
                        (k) => k !== optionKey
                      ),
                    }));
                    setFiltersChanged(true);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Grid>

      {/* Action buttons section */}
      <Grid 
        item 
        xs={12} 
        md={3} 
        lg={2}
        sx={{ 
          display: "flex", 
          justifyContent: { xs: "flex-start", md: "flex-end" },
          alignItems: "center",
          gap: 1,
          mt: { xs: 2, md: 0, lg: '-9px' }
        }}
      >
        <ResetButton
          onClick={resetFilters}
          status={fetchingDataLoader}
        />

        <UpdateButton
          onClick={() => {
            setFiltersChanged(false);
            setPage?.(1);
            setQueryFilter(filters);
            setUpdateData(true);
            setOrderBy('');
            setOrder('desc');
          }}
          filtersChanged={filtersChanged}
          status={fetchingDataLoader}
          disabled={isUpdateDisabled}
        />
      </Grid>
    </Grid>
  );
};

export default FilterSection;
